import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";

import { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";

import { selectCurrentUser } from "../../redux/user/user.selectors";
import { sessionExpired, updateToken } from "../../redux/user/user.actions";
import { authFetch, getApiUrl, SessionExpiredError } from "../../utils/api";
import { X, Sparkles } from "lucide-react";

import AccountInfoAdvisor from "../../components/account-info/account-info-advisor/account-info-advisor.component";

import { SectionWrapper } from "../../components/section-wrapper/sectionWrapper.styles";
import PageWrapper from "../../components/page-wrapper/page-wrapper.components";

const AdvisorInfoPage = ({ currentUser, onSessionExpired, onTokenRefresh }) => {
  const [userInfo, setUserInfo] = useState({});
  const [loadingError, setLoadingError] = useState(false);
  const [userWelcome, setUserWelcome] = useState(false);

  const toastIdRef = useRef(null);
  const apiUrl = getApiUrl();
  const authParams = { currentUser, onSessionExpired, onTokenRefresh };

  const fetchData = async () => {
    toastIdRef.current = toast.loading('Loading advisor info...');
    try {
      const response = await authFetch(
        `${apiUrl}/advisors?userId=${currentUser.id}`,
        {},
        authParams
      );
      if (response.ok) {
        const data = await response.json();
        toast.dismiss(toastIdRef.current);
        setUserInfo(data);
        setUserWelcome(data.hasWelcomePopup);
      } else {
        toast.error('Failed to load advisor info', { id: toastIdRef.current });
        throw new Error(response.statusText);
      }
    } catch (err) {
      toast.dismiss(toastIdRef.current);
      if (!(err instanceof SessionExpiredError)) {
        setLoadingError(true);
      }
    }
  };

    const updateUserWelcomePopup = async () => {
        setUserWelcome(false);
        try {
            await authFetch(
                `${apiUrl}/users/updateuserwelcomepopup/${currentUser.id}`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                },
                authParams
            );
        } catch (err) {
            if (!(err instanceof SessionExpiredError)) {
                console.error('Failed to update welcome popup:', err);
            }
        }
    };
    

  useEffect(() => {
    // guard: only fetch if currentUser has a valid token
    if (!currentUser?.token) return;

    fetchData();
    return () => {
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  return (
    <PageWrapper>
      {Object.keys(userInfo).length !== 0 && !loadingError ? (
        <div className="user__page-container">
          <AccountInfoAdvisor info={userInfo} currentUser={currentUser} />

        {/* welcome popup */}
        {userWelcome && !currentUser?.opsActor &&
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-modal w-full max-w-md overflow-hidden">
                    <div className="bg-gradient-to-br from-primary to-primary-dark px-6 pt-8 pb-6 text-center relative">
                        <button
                            onClick={updateUserWelcomePopup}
                            className="absolute top-3 right-3 p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                        >
                            <X className="w-4 h-4 text-white/70" />
                        </button>
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-white/20 rounded-2xl mb-4">
                            <Sparkles className="w-7 h-7 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-1">Welcome!</h3>
                        <p className="text-sm text-white/80">Your Advisor Portal has a new look</p>
                    </div>
                    <div className="px-6 py-5">
                        <p className="text-sm text-gray-600 mb-5">
                            A refreshed design with the same features and functions you rely on.
                        </p>
                        <button
                            onClick={updateUserWelcomePopup}
                            className="w-full bg-primary text-white py-3 px-6 rounded-full font-semibold text-sm hover:bg-primary-dark active:scale-[0.98] transition-all duration-200"
                        >
                            Got it
                        </button>
                    </div>
                </div>
            </div>
        }
        </div>
      ) : loadingError ? (
        <SectionWrapper>
          <h1 className="text-center">
            An error has occured while retrieving data. Please refresh the
            page and try again.
          </h1>
        </SectionWrapper>
      ) : null}
    </PageWrapper>
  );
};

const mapStateToProps = createStructuredSelector({
  currentUser: selectCurrentUser,
});

const mapDispatchToProps = (dispatch) => ({
  onSessionExpired: () => dispatch(sessionExpired()),
  onTokenRefresh: (token) => dispatch(updateToken(token)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdvisorInfoPage);
